/* eslint-disable react/jsx-no-target-blank */
import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {useNavigate} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useParticipant} from "../../hooks/useParticipant"
import {useBoothSession} from "../../hooks/useBoothSession"
import SplashForm, {PrivacyPolicyLink} from "../views/SplashForm"
import BigButton from "../buttons/BigButton"
import {Participant} from "../../library/Participant"
import GallerySample from "../views/GallerySample"
import {Box, Fade, Stack, Typography} from "@mui/material"
import {GalleryPrivacy} from "../../library/Gallery"

function SplashPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()

  const config = useConfig()
  const {participant, setParticipant} = useParticipant(config.presetCode)
  const {clearImages} = useBoothSession()

  const handleEnter = (newParticipant: Participant) => {
    clearImages()
    setParticipant(newParticipant)
    navigate(`/${config.presetCode}/camera`)
  }

  const theme = config.theme
  const {dataCapture} = config
  const showForm = dataCapture.emailOn || dataCapture.firstNameOn || dataCapture.lastNameOn || dataCapture.phoneNumberOn
  const buttonTitle = config.buttons.splashJoin ? config.buttons.splashJoin.title : t("splash.continue")
  const showGallerySample = config.gallery.privacy !== GalleryPrivacy.PRIVATE

  return (
    <Stack
      component="section"
      alignItems="center"
      sx={{
        minWidth: 320,
        width: "100%",
        maxWidth: "85%",
        mx: "auto",
      }}
    >
      {showGallerySample && (
        <Box sx={{mt: 2, width: "100%", maxWidth: 1024}}>
          <GallerySample
            gallery={config.gallery}
            readyThreshold={1}
            fallbackImageUrl={config.aiEffectSequence?._embedded.preview?._links.image.href}
          />
        </Box>
      )}

      <Fade in timeout={2000}>
        <Box
          component="header"
          sx={{
            mt: 3,
            textAlign: "center",
            maxWidth: {sm: 900},
          }}
        >
          <Typography
            component="h1"
            sx={{
              ...theme.textStyle,
              fontWeight: 600,
              fontSize: {xs: "28px", sm: "77px"},
              letterSpacing: {xs: "0.45px", sm: "1.2px"},
              lineHeight: {xs: "36px", sm: "97px"},
            }}
          >
            {theme.heading}
          </Typography>
          {theme.subheading && (
            <Typography
              component="h2"
              sx={{
                ...theme.textStyle,
                fontWeight: 300,
                letterSpacing: 0,
                fontSize: {xs: "23px", sm: "62px"},
                lineHeight: {xs: "25px", sm: "68px"},
                mt: {xs: 2, sm: "20px"},
              }}
            >
              {theme.subheading}
            </Typography>
          )}
        </Box>
      </Fade>

      <Fade in timeout={600}>
        <Box sx={{width: "100%", maxWidth: 500, mt: 5, px: "20px"}}>
          {showForm ? (
            <SplashForm
              theme={config.theme}
              currentParticipant={participant}
              emailOn={dataCapture.emailOn}
              firstNameOn={dataCapture.firstNameOn}
              lastNameOn={dataCapture.lastNameOn}
              phoneNumberOn={dataCapture.phoneNumberOn}
              handleSuccess={(participant) => handleEnter(participant)}
            />
          ) : (
            <Stack alignItems="center">
              <BigButton onClick={() => handleEnter(participant)} fullWidth>
                {buttonTitle}
              </BigButton>
              <PrivacyPolicyLink textColor={theme.textColor}/>
            </Stack>
          )}
        </Box>
      </Fade>

      {!config.hideAttribution && (
        <Box
          component="a"
          href={theme.poweredBy.url}
          target="_blank"
          sx={{
            ...theme.textStyle,
            display: "inline-block",
            mt: "10vh",
            fontSize: "0.78rem",
            fontWeight: 300,
            opacity: 0.9,
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": {textDecoration: "none"},
          }}
        >
          <Trans i18nKey="splash.poweredBy" values={{company: theme.poweredBy.entity}}>
            Powered by <Box component="span" sx={{letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 700}}>Simple Booth</Box>
          </Trans>
        </Box>
      )}
    </Stack>
  )
}

export default SplashPage
